import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { useTranslation } from 'react-i18next';
import 'react-easy-crop/react-easy-crop.css';
import './AvatarCropper.css';

/**
 * Utility function to create a cropped image
 */
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.src = url;
  });

/**
 * Generates a cropped image from the cropper area
 * pixelCrop from react-easy-crop contains x, y, width, height in pixels of the original image
 */
async function getCroppedImg(imageSrc, pixelCrop) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('No 2d context');
  }

  // react-easy-crop provides pixelCrop in pixels of the original image
  // Since we're cropping to square (aspect: 1), pixelCrop.width and height should be equal
  // But to be safe, we'll use the smaller dimension to ensure it's square
  const outputSize = Math.min(pixelCrop.width, pixelCrop.height);
  canvas.width = outputSize;
  canvas.height = outputSize;

  // Draw the cropped portion - pixelCrop is already in original image coordinates
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    outputSize, // Use square size for both width and height
    outputSize,
    0,
    0,
    outputSize,
    outputSize
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Canvas is empty'));
        return;
      }
      resolve(blob);
    }, 'image/jpeg', 0.95);
  });
}

function AvatarCropper({ imageSrc, onCropComplete, onCancel }) {
  const { t } = useTranslation(['profile', 'shared']);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [processing, setProcessing] = useState(false);

  const onCropChange = useCallback((crop) => {
    setCrop(crop);
  }, []);

  const onZoomChange = useCallback((zoom) => {
    setZoom(zoom);
  }, []);

  const onCropCompleteCallback = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleConfirm = async () => {
    if (!croppedAreaPixels) {
      return;
    }

    try {
      setProcessing(true);
      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);
      
      // Convert blob to File
      const file = new File([croppedImage], 'avatar.jpg', { type: 'image/jpeg' });
      
      onCropComplete(file);
    } catch (error) {
      console.error('Error cropping image:', error);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="avatar-cropper-modal">
      <div className="avatar-cropper-content">
        <div className="avatar-cropper-header">
          <h3>{t('profile.edit.cropAvatar')}</h3>
          <button 
            type="button"
            className="avatar-cropper-close"
            onClick={onCancel}
            disabled={processing}
          >
            ×
          </button>
        </div>
        
        <div className="avatar-cropper-container">
          <div className="avatar-cropper-wrapper">
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1} // Kwadrat 1:1
              onCropChange={onCropChange}
              onZoomChange={onZoomChange}
              onCropComplete={onCropCompleteCallback}
              cropShape="round" // Okrągły crop area
              showGrid={false}
              minZoom={0.5}
              maxZoom={3}
            />
          </div>
        </div>

        <div className="avatar-cropper-controls">
          <div className="avatar-cropper-zoom-control">
            <label htmlFor="zoom-slider">
              {t('profile.edit.zoom')}
            </label>
            <input
              id="zoom-slider"
              type="range"
              min={0.5}
              max={3}
              step={0.1}
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="avatar-cropper-slider"
              disabled={processing}
            />
          </div>

          <div className="avatar-cropper-actions">
            <button
              type="button"
              className="avatar-cropper-button avatar-cropper-button-cancel"
              onClick={onCancel}
              disabled={processing}
            >
              {t('shared.common.cancel')}
            </button>
            <button
              type="button"
              className="avatar-cropper-button avatar-cropper-button-confirm"
              onClick={handleConfirm}
              disabled={processing || !croppedAreaPixels}
            >
              {processing ? t('common.loading') : t('common.confirm')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AvatarCropper;
